import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";
import { z } from "zod";
import type { AgentCreate, AgentResponse } from "@/api";
import { createAgentMutation, getAgentQueryKey, listAgentsQueryKey, schemas, updateAgentMutation } from "@/api";
import { EntityFormCard, formText, IdentityFields } from "@/components/build/EntityForm";
import { useEntitySave } from "@/components/build/useEntitySave";
import { useAppForm } from "@/components/form/form";
import { Text } from "@/components/Text";
import { useCurrentUser } from "@/features/account/useCurrentUser";
import { NO_BRAND, useDefaultBrandOptions } from "@/features/build/brands/useDefaultBrandOptions";
import { useChatModels } from "@/features/chat/useChatModels";
import { SlashCommandsPreview } from "./SlashCommandsPreview";

// Form rules come from the GENERATED backend schemas (max lengths etc.), so
// the backend stays the source of truth. AgentUpdate is PATCH-shaped (every
// field optional/nullable); the form requires the fields it edits.
const update = schemas.zAgentUpdate.shape;
const agentFormSchema = z.object({
  name: update.name.unwrap().unwrap(),
  description: update.description,
  systemPrompt: update.systemPrompt.unwrap().unwrap(),
  modelId: update.modelId.unwrap().unwrap(),
  defaultBrandUuid: z.string(),
});
type AgentFormValues = z.infer<typeof agentFormSchema>;

/** A Build with AI draft opened in the create form ("Open in form" on its card). */
export interface AgentFormState {
  draft: Partial<AgentCreate>;
  /** The chat the draft came from; the created agent is linked to it. */
  sourceSessionUuid?: string;
}

/** The agent Overview form — the create page and the detail tab share it. */
export function AgentForm({ agent }: Readonly<{ agent?: AgentResponse }>) {
  const { t } = useTranslation("agents");
  const navigate = useNavigate();
  const fromChat = (useLocation().state as AgentFormState | null) ?? undefined;
  const seed = agent ? undefined : fromChat?.draft;
  const me = useCurrentUser();
  const models = useChatModels();
  const brandOptions = useDefaultBrandOptions(agent?.defaultBrand, Boolean(agent));
  const text = formText(t, t);
  const { saveExisting, createNew } = useEntitySave(text.messages);

  const create = useMutation(createAgentMutation());
  const updateMutation = useMutation(updateAgentMutation());

  const form = useAppForm({
    defaultValues: {
      name: agent?.name ?? seed?.name ?? "",
      description: agent?.description ?? seed?.description ?? "",
      systemPrompt: agent?.systemPrompt ?? seed?.systemPrompt ?? "",
      // Falls back to the resolved default (router tier) in the picker below
      // once models load; an untouched picker would otherwise submit "".
      modelId: agent?.modelId ?? seed?.modelId ?? models.selectedId ?? "",
      defaultBrandUuid: agent?.defaultBrand?.uuid ?? NO_BRAND,
    } as AgentFormValues,
    validators: { onChange: agentFormSchema },
    onSubmit: ({ value }) => {
      const base = {
        name: value.name,
        description: value.description || null,
        systemPrompt: value.systemPrompt,
        modelId: value.modelId,
      };
      if (agent) {
        return saveExisting({
          request: () =>
            updateMutation.mutateAsync({
              path: { agent_uuid: agent.uuid },
              body: { ...base, defaultBrandUuid: value.defaultBrandUuid || null },
              throwOnError: true,
            }),
          invalidate: [listAgentsQueryKey(), getAgentQueryKey({ path: { agent_uuid: agent.uuid } })],
          onSaved: () => form.reset(value),
        });
      }
      return createNew({
        request: async () => {
          const teamUuid = me.data?.teams.find((tm) => tm.team.isEveryone)?.team.uuid ?? me.data?.teams[0]?.team.uuid;
          if (!teamUuid) throw new Error("No team available");
          const created = await create.mutateAsync({
            body: { ...base, teamUuid, sourceSessionUuid: fromChat?.sourceSessionUuid },
            throwOnError: true,
          });
          return created.data.uuid;
        },
        invalidate: [listAgentsQueryKey()],
        to: (uuid) => `/build/agents/${uuid}`,
      });
    },
  });

  return (
    <EntityFormCard label={t("form.label")} onSubmit={() => void form.handleSubmit()}>
      <IdentityFields form={form} fields={{ name: "name", description: "description" }} labels={text.labels} />
      <form.AppField name="systemPrompt">
        {(field) => (
          <field.TextareaField
            label={t("form.instructions")}
            required
            placeholder={t("form.instructionsPlaceholder")}
            rows={8}
          />
        )}
      </form.AppField>
      <form.Subscribe selector={(state) => state.values.systemPrompt}>
        {(systemPrompt) => <SlashCommandsPreview systemPrompt={systemPrompt} />}
      </form.Subscribe>
      {/* key: remount the field once the default model resolves, so the
            controlled Select shows it (same pattern Settings uses). */}
      <form.AppField name="modelId" key={agent || seed?.modelId ? "set" : (models.selectedId ?? "loading")}>
        {(field) => (
          <field.SelectField
            label={t("form.model")}
            required
            options={models.options.map((o) => ({ value: o.id, label: o.label }))}
          />
        )}
      </form.AppField>
      {/* AgentCreate has no default brand: it's set once the agent exists. */}
      {agent && (
        <div className="flex flex-col gap-1">
          <form.AppField name="defaultBrandUuid">
            {(field) => <field.SelectField label={t("form.defaultBrand")} options={brandOptions} />}
          </form.AppField>
          <Text variant="caption" tone="tertiary">
            {t("form.defaultBrandHint")}
          </Text>
        </div>
      )}
      <form.AppForm>
        <form.SubmitActions
          onCancel={() => (agent ? form.reset() : navigate("/build/agents"))}
          submitLabel={agent ? undefined : t("form.create")}
          requireChanges={Boolean(agent)}
        />
      </form.AppForm>
    </EntityFormCard>
  );
}
